import { useUpdateSearchCandidates } from "apollo/hooks/mutations";
import { Auction, Patient } from "core/types";
import {
  shouldDisplayCandidates,
  useCandidates,
} from "dsl/atoms/SearchCandidates";
import PatientMenu, {
  handleSolutionsFromForm,
  isValidPatient,
  WatchedFieldContext,
} from "dsl/organisms/PatientMenu";
import type { FieldsHolderType } from "dsl/organisms/PatientMenu/transform";
import {
  extractPatientFields,
  getAuctionFromFields,
} from "dsl/organisms/PatientMenu/transform";
import { debounce } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

function EnhancedFormPatientMenu({
  auction,
  watchedFields,
}: {
  auction: Auction;
  watchedFields: FieldsHolderType;
}) {
  const [updateSearchCandidates, , , data] = useUpdateSearchCandidates();
  const { setCandidates } = useCandidates();

  const debouncedUpdateSearchCandidates = useCallback(
    debounce((prevAuction: Auction, newAuction: Auction) => {
      updateSearchCandidates({
        ...newAuction,
        id: prevAuction.id,
        patient: {
          ...newAuction.patient,
          id: prevAuction.patient?.id,
        },
        solutions: handleSolutionsFromForm(newAuction, prevAuction) ?? [],
        start_date: newAuction.start_date ?? prevAuction.start_date,
      });
    }, 500),
    [updateSearchCandidates],
  );

  useEffect(() => {
    const newAuction = getAuctionFromFields(watchedFields);
    if (
      isValidPatient(newAuction?.patient, auction) &&
      shouldDisplayCandidates(auction)
    ) {
      debouncedUpdateSearchCandidates(auction, newAuction);
    } else {
      setCandidates({
        candidates: undefined,
        patientId: auction.patient?.id ?? -1,
      });
    }
  }, [watchedFields, auction]);

  useEffect(() => {
    if (data) {
      setCandidates({
        candidates: data.searchCandidates?.count ?? 0,
        patientId: auction.patient?.id ?? -1,
      });
    }
  }, [data]);

  return null;
}

export function PatientAssessmentMenu({
  auction,
  children,
  patient,
}: {
  auction: Auction;
  children?: React.ReactNode;
  patient: Patient;
}) {
  const [watchedFields, setWatchedFields] = useState<FieldsHolderType>(() =>
    extractPatientFields(auction),
  );

  useEffect(() => {
    if (patient != null) {
      setWatchedFields(extractPatientFields(auction));
    }
  }, [patient, auction]);

  return (
    <WatchedFieldContext.Provider
      value={{
        setWatchedFields: (fields) => setWatchedFields(cloneDeep(fields)),
        watchedFields,
      }}
    >
      <EnhancedFormPatientMenu
        auction={auction}
        watchedFields={watchedFields}
      />
      <PatientMenu auction={auction} patient={patient} />
      <div className="flex flex-col">{children ?? <Outlet />}</div>
    </WatchedFieldContext.Provider>
  );
}
